import type { ChartOption } from "./chart-option.js"
import { ink, surface, type ThemeMode } from "../palette.js"
import { formatValue } from "../format.js"
import type { VizModel } from "../tile-model.js"
import { STALE_OPACITY, baseOption, tooltipFor } from "./base.js"

export type LineOptions = {
  mode: ThemeMode
  model: VizModel
  area?: boolean
  dataLabels?: "none" | "endpoints" | "extremes" | "all"
  legendPosition?: "bottom" | "right"
}

const LINE_WIDTH = 2
const SYMBOL_SIZE = 8

export function lineOption({
  mode,
  model,
  area = false,
  dataLabels = "endpoints",
  legendPosition,
}: LineOptions): ChartOption {
  const c = ink(mode)
  const base = baseOption({ mode, model, ...(legendPosition ? { legendPosition } : {}) })

  const series = model.series.map((s) => {
    const stale = s.state === "stale"
    const marks = labelIndices(s.data, dataLabels)

    return {
      type: "line" as const,
      name: s.name,
      data: s.data,
      smooth: false,
      // Missing periods break the line rather than being bridged — bridging invents the values in between
      connectNulls: false,
      symbol: "circle" as const,
      symbolSize: SYMBOL_SIZE,
      showSymbol: model.categories.length <= 40,
      lineStyle: { width: LINE_WIDTH, color: s.color, opacity: stale ? STALE_OPACITY : 1 },
      itemStyle: {
        color: s.color,
        borderColor: surface(mode),
        borderWidth: 2,
        opacity: stale ? STALE_OPACITY : 1,
      },
      ...(area
        ? {
            areaStyle: { color: s.color, opacity: stale ? 0.08 : 0.14 },
          }
        : {}),
      emphasis: { focus: "series" as const },
      label: {
        show: dataLabels !== "none",
        color: c.secondary,
        fontSize: 11,
        formatter: (p: { dataIndex: number; value: number | null }) =>
          p.value != null && marks.has(p.dataIndex)
            ? formatValue(p.value, { valueType: s.unit, context: "label" })
            : "",
      },
    }
  })

  return {
    ...base,
    tooltip: tooltipFor(mode, model, "axis"),
    series,
  }
}

function labelIndices(data: readonly (number | null)[], mode: LineOptions["dataLabels"]): Set<number> {
  const present = data.map((v, i) => (v == null ? -1 : i)).filter((i) => i >= 0)
  if (mode === "none" || present.length === 0) return new Set()
  if (mode === "all") return new Set(present)

  if (mode === "extremes") {
    let min = present[0]!
    let max = present[0]!
    for (const i of present) {
      if (data[i]! < data[min]!) min = i
      if (data[i]! > data[max]!) max = i
    }
    return new Set([min, max])
  }

  return new Set([present[0]!, present[present.length - 1]!])
}
